import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BuildIcon from '@material-ui/icons/Build';
import DnsIcon from '@material-ui/icons/Dns';
import SearchIcon from '@material-ui/icons/Search';
import TimelineIcon from '@material-ui/icons/Timeline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type ProductConnection = {
  label: string;
  status: 'connected' | 'gated';
  evidence: string;
  to?: string;
};

type ProductMapNode = {
  id: string;
  apiName: string;
  title: string;
  description: string;
  owner: string;
  lifecycle: string;
  type: string;
  system: string;
  maturity: string;
  featureClass: string;
  namespace: string;
  workflow: string;
  runtimeMappingMode: string;
  runtimeChildren: string[];
  runtimeChildrenWithTitles: string[];
  lighthouseAuditMode: string;
  lighthouseChildren: string[];
  lighthouseWebsiteUrl: string;
  validationCommands: string[];
  blockers: string[];
  connections: ProductConnection[];
};

type DocumentedOperation = {
  method: string;
  path: string;
  operationId: string;
  summary: string;
  tags: string[];
  state: 'declared-only';
};

type DocumentedRouteGroup = {
  group: string;
  operations: DocumentedOperation[];
};

type DocumentedProductApi = {
  productId: string;
  productTitle: string;
  apiName: string;
  apiTitle: string;
  owner: string;
  system: string;
  definitionSource: string;
  sourceLocation: string;
  serviceNames: string[];
  routeGroups: DocumentedRouteGroup[];
};

type LiveTraceOperation = {
  name: string;
  state: 'observed';
  category:
    | 'api-route'
    | 'frontend-route'
    | 'background-job'
    | 'database'
    | 'backstage-internal'
    | 'external-probe'
    | 'network'
    | 'runtime-internal';
  method?: string;
  path?: string;
};

type LiveTraceService = {
  name: string;
  productId: string;
  apiName: string;
  state: 'observed' | 'declared-only';
  operations: LiveTraceOperation[];
};

type ApiRouteGap = {
  productId: string;
  apiName: string;
  method: string;
  path: string;
  serviceName: string;
  state: 'documented-but-not-observed' | 'observed-but-undocumented';
  evidence: string;
};

type ApiRouteProbe = {
  productId: string;
  apiName: string;
  method: string;
  path: string;
  state:
    | 'already-observed'
    | 'safe-probe'
    | 'needs-fixture'
    | 'requires-auth'
    | 'unsafe-mutation';
  reason: string;
};

type ApiRouteProbePlan = {
  documentedRouteCount: number;
  observedRouteCount: number;
  safelyProbeableCount: number;
  blockedProbeCount: number;
  probes: ApiRouteProbe[];
};

type ApiTopologySummary = {
  generatedAt: string;
  documented: {
    products: DocumentedProductApi[];
  };
  live: {
    state: 'observed' | 'telemetry-unavailable';
    backend: 'jaeger';
    reason: string;
    services: LiveTraceService[];
    gaps: {
      documentedButNotObserved: ApiRouteGap[];
      observedButUndocumented: ApiRouteGap[];
    };
    probePlan: ApiRouteProbePlan;
  };
};

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'radial-gradient(circle at top left, rgba(251,140,0,0.22), transparent 34%), linear-gradient(135deg, rgba(0,121,107,0.20), rgba(21,101,192,0.12))',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(4),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  mapCanvas: {
    position: 'relative',
    display: 'grid',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 3,
    border: `1px solid ${theme.palette.divider}`,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    overflow: 'hidden',
  },
  tabs: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mindmapCanvas: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  mindmapProduct: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    background:
      'linear-gradient(135deg, rgba(0,121,107,0.16), rgba(21,101,192,0.10))',
  },
  mindmapApiNode: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
  },
  routeGroupGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  routeGroup: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  operationNode: {
    display: 'grid',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  operationHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  traceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: theme.spacing(2),
  },
  traceService: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  traceUnavailable: {
    marginBottom: theme.spacing(3),
    borderColor: theme.palette.warning.dark,
  },
  gapGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing(2),
  },
  graphToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  graphLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  graphViewport: {
    position: 'relative',
    minHeight: 680,
    overflowX: 'auto',
    overflowY: 'hidden',
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    background:
      'radial-gradient(circle at 18% 18%, rgba(251,140,0,0.16), transparent 22%), radial-gradient(circle at 82% 42%, rgba(0,121,107,0.18), transparent 26%), rgba(0,0,0,0.10)',
  },
  graphLayer: {
    position: 'relative',
    minWidth: 1080,
    height: 680,
  },
  graphEdges: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  graphEdge: {
    fill: 'none',
    stroke: theme.palette.divider,
    strokeWidth: 0.28,
    opacity: 0.48,
  },
  graphEdgeConnected: {
    stroke: theme.palette.primary.light,
    opacity: 0.82,
  },
  graphEdgeGated: {
    stroke: theme.palette.warning.main,
    strokeDasharray: '1.2 1.2',
    opacity: 0.65,
  },
  graphEdgeFocused: {
    stroke: theme.palette.secondary.light,
    strokeWidth: 0.42,
    opacity: 0.95,
  },
  graphNode: {
    position: 'absolute',
    width: 190,
    minHeight: 86,
    transform: 'translate(-50%, -50%)',
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: 'left',
    textTransform: 'none',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    boxShadow: theme.shadows[2],
    transition:
      'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, opacity 160ms ease',
    '&:hover': {
      transform: 'translate(-50%, -50%) scale(1.025)',
      boxShadow: theme.shadows[6],
    },
    '&.Mui-disabled': {
      color: theme.palette.text.secondary,
    },
  },
  graphProductNode: {
    borderColor: theme.palette.primary.main,
    background:
      'linear-gradient(145deg, rgba(0,121,107,0.28), rgba(0,0,0,0.08))',
  },
  graphHubNode: {
    width: 210,
    borderColor: theme.palette.secondary.main,
    background:
      'linear-gradient(145deg, rgba(251,140,0,0.24), rgba(0,0,0,0.08))',
  },
  graphSurfaceNode: {
    borderStyle: 'solid',
  },
  graphRuntimeNode: {
    width: 205,
    borderColor: theme.palette.info.light,
    background:
      'linear-gradient(145deg, rgba(21,101,192,0.22), rgba(0,0,0,0.08))',
  },
  graphNodeFocused: {
    borderColor: theme.palette.secondary.light,
    boxShadow: `0 0 0 2px ${theme.palette.secondary.main}`,
  },
  graphNodeGated: {
    borderColor: theme.palette.warning.dark,
    opacity: 0.76,
  },
  graphNodeTitle: {
    display: 'block',
    fontWeight: 700,
    lineHeight: 1.25,
  },
  graphNodeMeta: {
    display: 'block',
    marginTop: theme.spacing(0.75),
    fontSize: '0.72rem',
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  graphInspector: {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 0.9fr) minmax(280px, 1.1fr)',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  metaGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    marginTop: theme.spacing(2),
  },
  metaItem: {
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  childList: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  childItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  insightCard: {
    height: '100%',
  },
}));

function readString(value: unknown, emptyValue: string): string {
  return typeof value === 'string' && value.length > 0 ? value : emptyValue;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => (typeof item === 'string' ? item : undefined))
    .filter((item): item is string => Boolean(item));
}

function readAnnotation(
  annotations: Record<string, string> | undefined,
  key: string,
  emptyValue = 'not declared',
): string {
  return readString(annotations?.[key], emptyValue);
}

function splitAnnotationList(value: string): string[] {
  if (!value || value === 'not declared') {
    return [];
  }

  return value
    .split('|')
    .map(item => item.trim())
    .filter(Boolean);
}

function isDeclared(value: string): boolean {
  return value !== 'not declared' && value.length > 0;
}

function hasAnnotation(
  annotations: Record<string, string>,
  key: string,
): boolean {
  return isDeclared(readAnnotation(annotations, key));
}

function productConnection(
  label: string,
  connected: boolean,
  evidence: string,
  to?: string,
): ProductConnection {
  return {
    label,
    status: connected ? 'connected' : 'gated',
    evidence,
    to: connected ? to : undefined,
  };
}

function resolveChildTitle(
  childName: string,
  componentByName: Map<string, Entity>,
): string {
  const child = componentByName.get(childName);
  const title = readString(child?.metadata.title, childName);
  return title === childName ? childName : `${title} (${childName})`;
}

function productEntityToMapNode(
  entity: Entity,
  apiRepoIds: Set<string>,
  componentByName: Map<string, Entity>,
): ProductMapNode | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const repoId = annotations['c4ci.io/registry-repo-id'];
  if (typeof repoId !== 'string') {
    return undefined;
  }

  const apiName = `${repoId}-api`;
  const providesApis = readStringArray(entity.spec?.providesApis);
  const hasApi = providesApis.length > 0 || apiRepoIds.has(repoId);
  const hasTechDocs = hasAnnotation(annotations, 'backstage.io/techdocs-ref');
  const hasSource =
    hasAnnotation(annotations, 'github.com/project-slug') &&
    hasAnnotation(annotations, 'backstage.io/source-location');
  const hasDirectKubernetes =
    hasAnnotation(annotations, 'backstage.io/kubernetes-namespace') &&
    hasAnnotation(annotations, 'backstage.io/kubernetes-label-selector');
  const runtimeMappingMode = readAnnotation(
    annotations,
    'c4ci.io/runtime-mapping-mode',
  );
  const runtimeChildren = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/runtime-child-components', ''),
  );
  const lighthouseAuditMode = readAnnotation(
    annotations,
    'c4ci.io/lighthouse-audit-mode',
  );
  const lighthouseChildren = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/lighthouse-child-components', ''),
  );
  const lighthouseWebsiteUrl = readAnnotation(
    annotations,
    'lighthouse.com/website-url',
  );
  const hasChildRuntime =
    runtimeMappingMode === 'component-entities' && runtimeChildren.length > 0;
  const hasKubernetes = hasDirectKubernetes || hasChildRuntime;
  const hasLighthouse =
    isDeclared(lighthouseWebsiteUrl) ||
    (lighthouseAuditMode === 'component-entities' &&
      lighthouseChildren.length > 0);
  let kubernetesLink = '';
  if (hasDirectKubernetes) {
    kubernetesLink = `/catalog/default/component/${repoId}/kubernetes`;
  } else if (runtimeChildren[0]) {
    kubernetesLink = `/catalog/default/component/${runtimeChildren[0]}/kubernetes`;
  }

  let lighthouseLink = '';
  if (isDeclared(lighthouseWebsiteUrl)) {
    lighthouseLink = `/catalog/default/component/${repoId}/lighthouse`;
  } else if (lighthouseChildren[0]) {
    lighthouseLink = `/catalog/default/component/${lighthouseChildren[0]}/lighthouse`;
  }

  const sourceEvidence = readAnnotation(annotations, 'github.com/project-slug');
  const validationCommands = splitAnnotationList(
    readAnnotation(annotations, 'c4ci.io/validation-commands', ''),
  );
  const namespace = readAnnotation(annotations, 'c4ci.io/release-namespace');
  const workflow = readAnnotation(annotations, 'c4ci.io/deploy-workflow');
  const lighthouseEvidence = isDeclared(lighthouseWebsiteUrl)
    ? lighthouseWebsiteUrl
    : lighthouseChildren.join(', ') || lighthouseAuditMode;

  return {
    id: repoId,
    apiName,
    title: readString(entity.metadata.title, repoId),
    description: readString(
      entity.metadata.description,
      'No product description declared in catalog.',
    ),
    owner: readString(entity.spec?.owner, 'unassigned'),
    lifecycle: readString(entity.spec?.lifecycle, 'unknown'),
    type: readString(entity.spec?.type, 'unknown'),
    system: readString(entity.spec?.system, 'not declared'),
    maturity: readAnnotation(annotations, 'c4ci.io/delivery-maturity'),
    featureClass: readAnnotation(annotations, 'c4ci.io/max-feature-class'),
    namespace,
    workflow,
    runtimeMappingMode,
    runtimeChildren,
    runtimeChildrenWithTitles: runtimeChildren.map(childName =>
      resolveChildTitle(childName, componentByName),
    ),
    lighthouseAuditMode,
    lighthouseChildren,
    lighthouseWebsiteUrl,
    validationCommands,
    blockers: splitAnnotationList(
      readAnnotation(annotations, 'c4ci.io/blocking-gaps', ''),
    ),
    connections: [
      productConnection(
        'Catalog',
        true,
        repoId,
        `/catalog/default/component/${repoId}`,
      ),
      productConnection(
        'TechDocs',
        hasTechDocs,
        readAnnotation(annotations, 'backstage.io/techdocs-ref'),
        `/docs/default/component/${repoId}`,
      ),
      productConnection(
        'API',
        hasApi,
        hasApi ? apiName : 'no API entity linked',
        `/catalog/default/api/${apiName}`,
      ),
      productConnection(
        'CI/CD',
        hasSource,
        sourceEvidence,
        `/catalog/default/component/${repoId}/ci-cd`,
      ),
      productConnection(
        'TODOs',
        hasSource,
        sourceEvidence,
        `/catalog/default/component/${repoId}/todos`,
      ),
      productConnection(
        'Scorecards',
        true,
        'catalog-backed Tech Insights checks',
        `/catalog/default/component/${repoId}/scorecards`,
      ),
      productConnection(
        'Kubernetes',
        hasKubernetes,
        hasDirectKubernetes ? namespace : runtimeChildren.join(', '),
        kubernetesLink,
      ),
      productConnection(
        'Lighthouse',
        hasLighthouse,
        lighthouseEvidence,
        lighthouseLink,
      ),
      productConnection(
        'Release Evidence',
        true,
        workflow,
        '/release-evidence',
      ),
    ],
  };
}

const MapMetric = ({ label, value }: { label: string; value: string }) => {
  const classes = useStyles();

  return (
    <Box className={classes.metaItem}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

function classNames(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function productGraphY(index: number, total: number): number {
  if (total <= 1) {
    return 50;
  }

  return 18 + (index * 64) / (total - 1);
}

function connectionGraphPoint(index: number): { x: number; y: number } {
  const points = [
    { x: 56, y: 12 },
    { x: 56, y: 30 },
    { x: 56, y: 48 },
    { x: 56, y: 66 },
    { x: 56, y: 84 },
    { x: 73, y: 20 },
    { x: 73, y: 40 },
    { x: 73, y: 60 },
    { x: 73, y: 80 },
  ];

  return points[index] ?? { x: 73, y: 90 };
}

function graphPath(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): string {
  const midX = (fromX + toX) / 2;
  return `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`;
}

function shortEvidence(value: string): string {
  if (value.length <= 46) {
    return value;
  }

  return `${value.slice(0, 43)}...`;
}

function preferredFocusProductId(products: ProductMapNode[]): string {
  const aggregateProduct = products.find(
    product =>
      product.runtimeMappingMode === 'component-entities' ||
      product.runtimeChildren.length > 0,
  );

  return aggregateProduct?.id ?? products[0]?.id ?? '';
}

const ProductFocusInspector = ({ product }: { product: ProductMapNode }) => {
  const classes = useStyles();

  return (
    <Box className={classes.graphInspector}>
      <InfoCard title={`Focused product: ${product.title}`}>
        <Typography variant="body2" color="textSecondary" paragraph>
          {product.description}
        </Typography>
        <div className={classes.metaGrid}>
          <MapMetric label="Entity" value={product.id} />
          <MapMetric label="Owner" value={product.owner} />
          <MapMetric label="Lifecycle" value={product.lifecycle} />
          <MapMetric label="Type" value={product.type} />
          <MapMetric label="System" value={product.system} />
          <MapMetric label="Feature Class" value={product.featureClass} />
        </div>
      </InfoCard>

      <InfoCard title="Runtime proof for focused product">
        <div className={classes.metaGrid}>
          <MapMetric
            label="Runtime Mapping"
            value={product.runtimeMappingMode}
          />
          <MapMetric label="Namespace" value={product.namespace} />
          <MapMetric label="Workflow" value={product.workflow} />
          <MapMetric
            label="Lighthouse Mode"
            value={product.lighthouseAuditMode}
          />
        </div>
        {isDeclared(product.lighthouseWebsiteUrl) && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              Lighthouse URL
            </Typography>
            <Typography variant="body2">
              <Link to={product.lighthouseWebsiteUrl}>
                {product.lighthouseWebsiteUrl}
              </Link>
            </Typography>
          </Box>
        )}
        <Box mt={2}>
          <Typography variant="subtitle2">Child runtime components</Typography>
          <div className={classes.childList}>
            {product.runtimeChildrenWithTitles.length > 0 ? (
              product.runtimeChildrenWithTitles.map(child => (
                <Box className={classes.childItem} key={child}>
                  <BuildIcon fontSize="small" />
                  <Typography variant="body2">{child}</Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No child component mapping declared; product maps directly
                through its catalog entity.
              </Typography>
            )}
          </div>
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle2">Known blockers</Typography>
          {product.blockers.length > 0 ? (
            product.blockers.map(blocker => (
              <Typography color="textSecondary" key={blocker} variant="body2">
                {blocker}
              </Typography>
            ))
          ) : (
            <Typography color="textSecondary" variant="body2">
              No blocking gaps declared in product metadata.
            </Typography>
          )}
        </Box>
      </InfoCard>
    </Box>
  );
};

const ProductGraphCanvas = ({
  products,
  focusedProductId,
  setFocusedProductId,
}: {
  products: ProductMapNode[];
  focusedProductId: string;
  setFocusedProductId: (productId: string) => void;
}) => {
  const classes = useStyles();
  const focusedProduct =
    products.find(product => product.id === focusedProductId) ?? products[0];
  const productPointX = 14;
  const hubPoint = { x: 38, y: 50 };
  const proofPoint = { x: 89, y: 50 };

  if (!focusedProduct) {
    return null;
  }

  return (
    <Box className={classes.mapCanvas}>
      <Box className={classes.graphToolbar}>
        <div>
          <Typography variant="h5" component="h3">
            Interactive Product Graph
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click a product node to refocus the graph; connected surface nodes
            route to their live Backstage views, and gated nodes stay disabled.
          </Typography>
        </div>
        <div className={classes.graphLegend}>
          <Chip color="primary" label="Connected" size="small" />
          <Chip label="Focused" size="small" />
          <Chip label="Gated by real data" size="small" variant="outlined" />
        </div>
      </Box>

      <Box className={classes.graphViewport}>
        <Box className={classes.graphLayer}>
          <svg
            aria-hidden="true"
            className={classes.graphEdges}
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
          >
            {products.map((product, index) => {
              const productY = productGraphY(index, products.length);
              const isFocused = product.id === focusedProduct.id;

              return (
                <path
                  className={classNames(
                    classes.graphEdge,
                    isFocused && classes.graphEdgeFocused,
                  )}
                  d={graphPath(productPointX, productY, hubPoint.x, hubPoint.y)}
                  key={`product-${product.id}`}
                />
              );
            })}
            {focusedProduct.connections.map((connection, index) => {
              const point = connectionGraphPoint(index);
              const isConnected = connection.status === 'connected';

              return (
                <path
                  className={classNames(
                    classes.graphEdge,
                    isConnected
                      ? classes.graphEdgeConnected
                      : classes.graphEdgeGated,
                  )}
                  d={graphPath(hubPoint.x, hubPoint.y, point.x, point.y)}
                  key={`surface-${connection.label}`}
                />
              );
            })}
            {focusedProduct.connections
              .filter(connection =>
                ['Kubernetes', 'Lighthouse', 'Release Evidence'].includes(
                  connection.label,
                ),
              )
              .map(connection => {
                const index = focusedProduct.connections.indexOf(connection);
                const point = connectionGraphPoint(index);

                return (
                  <path
                    className={classNames(
                      classes.graphEdge,
                      connection.status === 'connected'
                        ? classes.graphEdgeConnected
                        : classes.graphEdgeGated,
                    )}
                    d={graphPath(point.x, point.y, proofPoint.x, proofPoint.y)}
                    key={`proof-${connection.label}`}
                  />
                );
              })}
          </svg>

          {products.map((product, index) => {
            const isFocused = product.id === focusedProduct.id;

            return (
              <Button
                aria-label={`Focus product ${product.id}`}
                aria-pressed={isFocused}
                className={classNames(
                  classes.graphNode,
                  classes.graphProductNode,
                  isFocused && classes.graphNodeFocused,
                )}
                data-testid="product-graph-node"
                key={product.id}
                onClick={() => setFocusedProductId(product.id)}
                style={{
                  left: `${productPointX}%`,
                  top: `${productGraphY(index, products.length)}%`,
                }}
              >
                <span>
                  <span className={classes.graphNodeTitle}>
                    {product.title}
                  </span>
                  <span className={classes.graphNodeMeta}>
                    {product.id} · {product.maturity}
                  </span>
                </span>
              </Button>
            );
          })}

          <Box
            className={classNames(classes.graphNode, classes.graphHubNode)}
            style={{ left: `${hubPoint.x}%`, top: `${hubPoint.y}%` }}
          >
            <span className={classes.graphNodeTitle}>
              {focusedProduct.title}
            </span>
            <span className={classes.graphNodeMeta}>
              Focused catalog entity · {focusedProduct.lifecycle}
            </span>
          </Box>

          {focusedProduct.connections.map((connection, index) => {
            const point = connectionGraphPoint(index);
            const targetRoute = connection.to;
            const isConnected =
              connection.status === 'connected' && Boolean(targetRoute);
            const nodeClassName = classNames(
              classes.graphNode,
              classes.graphSurfaceNode,
              !isConnected && classes.graphNodeGated,
            );
            const content = (
              <span>
                <span className={classes.graphNodeTitle}>
                  {connection.label}
                </span>
                <span className={classes.graphNodeMeta}>
                  {connection.status} · {shortEvidence(connection.evidence)}
                </span>
              </span>
            );

            if (!isConnected || !targetRoute) {
              return (
                <Button
                  className={nodeClassName}
                  disabled
                  key={connection.label}
                  style={{ left: `${point.x}%`, top: `${point.y}%` }}
                >
                  {content}
                </Button>
              );
            }

            return (
              <Button
                className={nodeClassName}
                color="primary"
                component={RouterLink}
                key={connection.label}
                style={{ left: `${point.x}%`, top: `${point.y}%` }}
                to={targetRoute!}
              >
                {content}
              </Button>
            );
          })}

          <Box
            className={classNames(classes.graphNode, classes.graphRuntimeNode)}
            style={{ left: `${proofPoint.x}%`, top: `${proofPoint.y}%` }}
          >
            <span className={classes.graphNodeTitle}>Runtime proof</span>
            <span className={classes.graphNodeMeta}>
              {focusedProduct.namespace} · {focusedProduct.workflow}
            </span>
          </Box>
        </Box>
      </Box>

      <ProductFocusInspector product={focusedProduct} />
    </Box>
  );
};

function productsForFocus(
  summary: ApiTopologySummary,
  focusedProductId: string,
): DocumentedProductApi[] {
  if (!focusedProductId) {
    return summary.documented.products;
  }

  return summary.documented.products.filter(
    product => product.productId === focusedProductId,
  );
}

function servicesForFocus(
  summary: ApiTopologySummary,
  focusedProductId: string,
): LiveTraceService[] {
  if (!focusedProductId) {
    return summary.live.services;
  }

  return summary.live.services.filter(
    service => service.productId === focusedProductId,
  );
}

function documentedOperationCount(summary?: ApiTopologySummary): number {
  return (
    summary?.documented.products.reduce(
      (total, product) =>
        total +
        product.routeGroups.reduce(
          (groupTotal, group) => groupTotal + group.operations.length,
          0,
        ),
      0,
    ) ?? 0
  );
}

const DocumentedApiMindmap = ({
  focusedProductId,
  summary,
}: {
  focusedProductId: string;
  summary: ApiTopologySummary;
}) => {
  const classes = useStyles();
  const products = productsForFocus(summary, focusedProductId);

  if (products.length === 0) {
    return (
      <InfoCard title="Documented API Mindmap">
        <Typography variant="body2" color="textSecondary">
          No documented product APIs were returned for this focused product.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <Box className={classes.mindmapCanvas}>
      <ContentHeader title="Documented API Mindmap" />
      {products.map(product => (
        <Box className={classes.mindmapProduct} key={product.apiName}>
          <Typography variant="h5">{product.productTitle}</Typography>
          <Typography variant="body2" color="textSecondary">
            {product.productId} · owner {product.owner || 'not declared'} ·
            system {product.system || 'not declared'}
          </Typography>
          <Box className={classes.mindmapApiNode}>
            <Box className={classes.operationHeader}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6">{product.apiName}</Typography>
              <Chip label="OpenAPI" size="small" />
              <Chip label="declared-only" size="small" variant="outlined" />
            </Box>
            <Typography variant="body2" color="textSecondary">
              Definition source: {product.definitionSource || 'not declared'}
            </Typography>
            {product.serviceNames.length > 0 && (
              <Box className={classes.chipRow} mt={2}>
                {product.serviceNames.map(serviceName => (
                  <Chip
                    key={serviceName}
                    label={`trace service: ${serviceName}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            <div className={classes.routeGroupGrid}>
              {product.routeGroups.map(group => (
                <Box className={classes.routeGroup} key={group.group}>
                  <Typography variant="subtitle2">
                    Route group {group.group}
                  </Typography>
                  {group.operations.map(operation => (
                    <Box
                      className={classes.operationNode}
                      key={`${operation.method}:${operation.path}`}
                    >
                      <div className={classes.operationHeader}>
                        <Chip
                          color="primary"
                          label={operation.method}
                          size="small"
                        />
                        <Typography variant="body2">
                          {operation.path}
                        </Typography>
                        <Chip
                          label={operation.state}
                          size="small"
                          variant="outlined"
                        />
                      </div>
                      <Typography variant="body2" color="textSecondary">
                        {operation.summary}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {operation.operationId}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </div>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const LiveApiTraceMap = ({
  focusedProductId,
  summary,
}: {
  focusedProductId: string;
  summary: ApiTopologySummary;
}) => {
  const classes = useStyles();
  const services = servicesForFocus(summary, focusedProductId);
  const observedOperationCount = services.reduce(
    (total, service) => total + service.operations.length,
    0,
  );
  const observedApiRouteCount = services.reduce(
    (total, service) =>
      total +
      service.operations.filter(operation => operation.category === 'api-route')
        .length,
    0,
  );

  return (
    <Box>
      <ContentHeader title="Live API Traces" />
      <div className={classes.chipRow}>
        <Chip label="Trace category: api-route" size="small" />
        <Chip label="Trace category: database" size="small" variant="outlined" />
        <Chip
          label="Trace category: backstage-internal"
          size="small"
          variant="outlined"
        />
      </div>
      {summary.live.state === 'telemetry-unavailable' && (
        <InfoCard
          className={classes.traceUnavailable}
          title="telemetry-unavailable"
        >
          <Typography variant="body2" color="textSecondary">
            {summary.live.reason}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Declared services remain visible below, but no observed route calls
            are synthesized without Jaeger evidence.
          </Typography>
        </InfoCard>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <InfoCard title="Trace Backend">
            <Typography variant="h5">{summary.live.backend}</Typography>
            <Typography variant="body2" color="textSecondary">
              Live API calls must come from the configured OpenTelemetry trace
              backend.
            </Typography>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard title="Declared Services">
            <Typography variant="h5">{summary.live.services.length}</Typography>
            <Typography variant="body2" color="textSecondary">
              OTEL service names projected from product metadata.
            </Typography>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard title="Observed API Routes">
            <Typography variant="h5">{observedApiRouteCount}</Typography>
            <Typography variant="body2" color="textSecondary">
              Route-like operations returned by Jaeger for the focused product.
            </Typography>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <InfoCard title="Observed Operations">
            <Typography variant="h5">{observedOperationCount}</Typography>
            <Typography variant="body2" color="textSecondary">
              All observed spans after trace category classification.
            </Typography>
          </InfoCard>
        </Grid>
      </Grid>
      <Box className={classes.traceGrid} mt={3}>
        {services.map(service => (
          <Box className={classes.traceService} key={service.name}>
            <Box className={classes.operationHeader}>
              <TimelineIcon color="primary" />
              <Typography variant="h6">{service.name}</Typography>
              <Chip
                label={service.state}
                size="small"
                variant={service.state === 'observed' ? 'default' : 'outlined'}
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {service.productId} · {service.apiName}
            </Typography>
            {service.operations.length > 0 ? (
              service.operations.map(operation => (
                <Box className={classes.operationNode} key={operation.name}>
                  <div className={classes.operationHeader}>
                    <Chip color="primary" label="observed" size="small" />
                    <Chip
                      label={`Trace category: ${operation.category}`}
                      size="small"
                      variant="outlined"
                    />
                    {operation.method && (
                      <Chip label={operation.method} size="small" />
                    )}
                    <Typography variant="body2">{operation.name}</Typography>
                  </div>
                  {operation.path && (
                    <Typography variant="caption" color="textSecondary">
                      normalized route: {operation.path}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                declared-only: no observed operations returned for this service.
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ApiRouteGapAnalysis = ({
  focusedProductId,
  summary,
}: {
  focusedProductId: string;
  summary: ApiTopologySummary;
}) => {
  const classes = useStyles();
  const filterGap = (gap: ApiRouteGap) =>
    !focusedProductId || gap.productId === focusedProductId;
  const documentedButNotObserved =
    summary.live.gaps.documentedButNotObserved.filter(filterGap);
  const observedButUndocumented =
    summary.live.gaps.observedButUndocumented.filter(filterGap);
  const probes = summary.live.probePlan.probes.filter(probe =>
    !focusedProductId || probe.productId === focusedProductId,
  );
  const safeProbeCount = probes.filter(
    probe => probe.state === 'safe-probe',
  ).length;
  const observedProbeCount = probes.filter(
    probe => probe.state === 'already-observed',
  ).length;
  const blockedProbeCount =
    probes.length - safeProbeCount - observedProbeCount;

  const renderGap = (gap: ApiRouteGap) => (
    <Box className={classes.operationNode} key={`${gap.state}:${gap.method}:${gap.path}:${gap.evidence}`}>
      <div className={classes.operationHeader}>
        <Chip label={gap.productId} size="small" />
        <Chip color="primary" label={gap.method} size="small" />
        <Typography variant="body2">{gap.path}</Typography>
      </div>
      <Typography variant="caption" color="textSecondary">
        {gap.serviceName} · {gap.evidence}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <ContentHeader title="API Route Gaps" />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InfoCard title="Synthetic Route Probe Plan">
            <Typography variant="body2" color="textSecondary" paragraph>
              Factory classifies documented API routes before any synthetic
              traffic is generated. Generic probes are allowed only for
              read-only routes without path parameters; mutation, auth-sensitive,
              and fixture-dependent routes stay blocked until a product-owned
              probe contract exists.
            </Typography>
            <div className={classes.chipRow}>
              <Chip
                color="primary"
                label={`${observedProbeCount} already observed`}
                size="small"
              />
              <Chip
                label={`${safeProbeCount} safe probes`}
                size="small"
                variant="outlined"
              />
              <Chip
                color="secondary"
                label={`${blockedProbeCount} blocked`}
                size="small"
                variant="outlined"
              />
            </div>
            <div className={classes.gapGrid}>
              {probes.slice(0, 24).map(probe => (
                <Box
                  className={classes.operationNode}
                  key={`${probe.productId}:${probe.method}:${probe.path}`}
                >
                  <div className={classes.operationHeader}>
                    <Chip label={probe.productId} size="small" />
                    <Chip color="primary" label={probe.method} size="small" />
                    <Chip label={probe.state} size="small" variant="outlined" />
                    <Typography variant="body2">{probe.path}</Typography>
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {probe.reason}
                  </Typography>
                </Box>
              ))}
            </div>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title="Documented but not observed">
            <Typography variant="body2" color="textSecondary" paragraph>
              OpenAPI routes that exist in catalog docs but have not appeared in
              live traces for the selected product.
            </Typography>
            <div className={classes.gapGrid}>
              {documentedButNotObserved.length > 0 ? (
                documentedButNotObserved.map(renderGap)
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No documented-only API route gaps for this focus.
                </Typography>
              )}
            </div>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title="Observed but undocumented">
            <Typography variant="body2" color="textSecondary" paragraph>
              Live API routes seen in traces that do not match the registered
              OpenAPI contract.
            </Typography>
            <div className={classes.gapGrid}>
              {observedButUndocumented.length > 0 ? (
                observedButUndocumented.map(renderGap)
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No undocumented observed API routes for this focus.
                </Typography>
              )}
            </div>
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export const FactoryProductVisualizerPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [products, setProducts] = useState<ProductMapNode[]>([]);
  const [focusedProductId, setFocusedProductId] = useState('');
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [error, setError] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [apiTopology, setApiTopology] = useState<ApiTopologySummary>();
  const [apiTopologyState, setApiTopologyState] = useState<
    'loading' | 'loaded' | 'error'
  >('loading');
  const [apiTopologyError, setApiTopologyError] = useState<string>();

  useEffect(() => {
    let isActive = true;

    Promise.all([
      catalogApi.getEntities({
        filter: { kind: 'Component' },
        order: { field: 'metadata.name', order: 'asc' },
      }),
      catalogApi.getEntities({
        filter: { kind: 'API' },
        order: { field: 'metadata.name', order: 'asc' },
      }),
    ])
      .then(([componentResponse, apiResponse]) => {
        if (!isActive) {
          return;
        }

        const componentByName = new Map(
          componentResponse.items.map(component => [
            component.metadata.name,
            component,
          ]),
        );
        const apiRepoIds = new Set(
          apiResponse.items
            .map(api => api.metadata.annotations?.['c4ci.io/registry-repo-id'])
            .filter((repoId): repoId is string => Boolean(repoId)),
        );
        const catalogProducts = componentResponse.items
          .map(component =>
            productEntityToMapNode(component, apiRepoIds, componentByName),
          )
          .filter((product): product is ProductMapNode => Boolean(product));

        if (catalogProducts.length === 0) {
          setProducts([]);
          setState('error');
          setError(
            'No registry-backed product components with visualizer metadata were returned by catalog.',
          );
          return;
        }

        setProducts(catalogProducts);
        setFocusedProductId(currentFocusedProductId =>
          catalogProducts.some(
            product => product.id === currentFocusedProductId,
          )
            ? currentFocusedProductId
            : preferredFocusProductId(catalogProducts),
        );
        setState('loaded');
        setError(undefined);
      })
      .catch(catalogError => {
        if (!isActive) {
          return;
        }

        setProducts([]);
        setFocusedProductId('');
        setState('error');
        setError(
          catalogError instanceof Error
            ? catalogError.message
            : String(catalogError),
        );
      });

    return () => {
      isActive = false;
    };
  }, [catalogApi]);

  useEffect(() => {
    let isActive = true;

    discoveryApi
      .getBaseUrl('c4ci-api-topology')
      .then(baseUrl => fetchApi.fetch(`${baseUrl}/v1/summary`))
      .then(async response => {
        if (!response.ok) {
          throw new Error(
            `API topology request failed with ${response.status}`,
          );
        }
        return (await response.json()) as ApiTopologySummary;
      })
      .then(summary => {
        if (!isActive) {
          return;
        }
        setApiTopology(summary);
        setApiTopologyState('loaded');
        setApiTopologyError(undefined);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setApiTopology(undefined);
        setApiTopologyState('error');
        setApiTopologyError(
          'The Factory API topology endpoint is unavailable; documented and live API maps require the backend route.',
        );
      });

    return () => {
      isActive = false;
    };
  }, [discoveryApi, fetchApi]);

  const runtimeChildCount = products.reduce(
    (total, product) => total + product.runtimeChildren.length,
    0,
  );
  const apiOperationCount = documentedOperationCount(apiTopology);

  return (
    <Page themeId="home">
      <Header
        title="Product Visualizer"
        subtitle="Catalog-backed product relationships, runtime mappings, and proof surfaces for C4CI products"
      />
      <Content>
        <Box className={classes.hero}>
          <div className={classes.chipRow}>
            <Chip color="primary" label="Catalog-backed product visualizer" />
            <Chip label="Documented API Mindmap" variant="outlined" />
            <Chip label="Live API Traces" variant="outlined" />
            <Chip
              label={
                state === 'loaded'
                  ? 'Live catalog data'
                  : 'Catalog data required'
              }
              variant="outlined"
            />
            <Chip label="No diagram placeholders" variant="outlined" />
          </div>
          <Typography variant="h4" component="h2" gutterBottom>
            Product System Map
          </Typography>
          <Typography variant="body1" color="textSecondary">
            This visualizer maps each registered C4CI product to its Backstage
            Catalog entity, API entity, TechDocs, CI/CD, TODO scanning,
            scorecards, runtime AKS surface, Lighthouse surface, and release
            evidence. Its interactive product graph reads only catalog
            annotations and registered entities, so missing links show as gated
            instead of pretending they exist.
          </Typography>
        </Box>

        {state === 'loading' && <Progress />}
        {error && (
          <Box mb={3}>
            <InfoCard title="Live Catalog Data Required">
              <Typography variant="body2" color="textSecondary">
                Product Visualizer did not render the map because live catalog
                metadata was unavailable: {error}
              </Typography>
            </InfoCard>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <InfoCard className={classes.insightCard} title="Mapped Products">
              <Box display="flex" alignItems="center" mb={2}>
                <AccountTreeIcon color="primary" />
              </Box>
              <Typography variant="h3">{products.length}</Typography>
              <Typography variant="body2" color="textSecondary">
                Registry-backed product components returned by catalog.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoCard className={classes.insightCard} title="Runtime Children">
              <Box display="flex" alignItems="center" mb={2}>
                <DnsIcon color="primary" />
              </Box>
              <Typography variant="h3">{runtimeChildCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Child component entities used for aggregate product runtime
                mappings such as Arch.
              </Typography>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoCard
              className={classes.insightCard}
              title="Documented Operations"
            >
              <Box display="flex" alignItems="center" mb={2}>
                <AssessmentIcon color="primary" />
              </Box>
              <Typography variant="h3">{apiOperationCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Documented API operations parsed from catalog OpenAPI
                definitions.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>

        <Tabs
          className={classes.tabs}
          indicatorColor="primary"
          onChange={(_event, value) => setActiveTab(value)}
          textColor="primary"
          value={activeTab}
        >
          <Tab label="Product Graph" />
          <Tab label="Documented API Mindmap" />
          <Tab label="Live API Traces" />
          <Tab label="API Route Gaps" />
        </Tabs>

        {activeTab === 0 && (
          <Box mt={4}>
            <ContentHeader title="Product System Map" />
            <ProductGraphCanvas
              focusedProductId={focusedProductId}
              products={products}
              setFocusedProductId={setFocusedProductId}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box mt={4}>
            {apiTopologyState === 'loading' && <Progress />}
            {apiTopologyState === 'error' && (
              <InfoCard title="Documented API Mindmap">
                <Typography variant="body2" color="textSecondary">
                  {apiTopologyError}
                </Typography>
              </InfoCard>
            )}
            {apiTopologyState === 'loaded' && apiTopology && (
              <DocumentedApiMindmap
                focusedProductId={focusedProductId}
                summary={apiTopology}
              />
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box mt={4}>
            {apiTopologyState === 'loading' && <Progress />}
            {apiTopologyState === 'error' && (
              <InfoCard title="Live API Traces">
                <Typography variant="body2" color="textSecondary">
                  {apiTopologyError}
                </Typography>
              </InfoCard>
            )}
            {apiTopologyState === 'loaded' && apiTopology && (
              <LiveApiTraceMap
                focusedProductId={focusedProductId}
                summary={apiTopology}
              />
            )}
          </Box>
        )}

        {activeTab === 3 && (
          <Box mt={4}>
            {apiTopologyState === 'loading' && <Progress />}
            {apiTopologyState === 'error' && (
              <InfoCard title="API Route Gaps">
                <Typography variant="body2" color="textSecondary">
                  {apiTopologyError}
                </Typography>
              </InfoCard>
            )}
            {apiTopologyState === 'loaded' && apiTopology && (
              <ApiRouteGapAnalysis
                focusedProductId={focusedProductId}
                summary={apiTopology}
              />
            )}
          </Box>
        )}

        <Box mt={4}>
          <InfoCard title="How To Read This Map">
            <Typography variant="body2" color="textSecondary" paragraph>
              The left rail contains product nodes, the center graph fan-out
              shows Backstage feature surfaces for the focused product, and the
              right node shows runtime proof. Direct products use their own AKS
              and Lighthouse annotations; aggregate products can point to child
              runtime components instead.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              For broad architecture graph exploration, use{' '}
              <Link component={RouterLink} to="/catalog-graph">
                Catalog Graph
              </Link>
              . For Backstage app-extension debugging, use{' '}
              <Link component={RouterLink} to="/visualizer">
                App Visualizer
              </Link>
              . This page is intentionally the product map.
            </Typography>
          </InfoCard>
        </Box>

        <Box mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <InfoCard className={classes.insightCard} title="Discovery">
                <SearchIcon color="primary" />
                <Typography variant="body2" color="textSecondary">
                  Product nodes stay discoverable through Catalog, API Docs,
                  TechDocs, TODOs, Linguist, and Scorecards when the catalog
                  entity carries the required source and docs annotations.
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard className={classes.insightCard} title="Delivery">
                <TimelineIcon color="primary" />
                <Typography variant="body2" color="textSecondary">
                  Deployment workflows, release evidence, and validation counts
                  come from product repo metadata, not from local page fixtures.
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard className={classes.insightCard} title="Assurance">
                <VerifiedUserIcon color="primary" />
                <Typography variant="body2" color="textSecondary">
                  Runtime, Lighthouse, and blocker sections expose what is
                  proved today and which integrations are still gated by real
                  product-owned data.
                </Typography>
              </InfoCard>
            </Grid>
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};
